import * as XLSX from 'xlsx';

const COLUMNS = [
  'Component Code *', 'Component Name *', 'Description', 'Salary Slip Description', 
  'Earning Category (Fixed/Variable/None)', 'Component Type (Allowance/Deduction/Contribution) *',
  'Account Number', 'Calculation Method (Fixed/Daily-Based) *', 'Default Amount', 'Default Percentage',
  'Taxable (Yes/No)', 'EPF (Yes/No)', 'ETF (Yes/No)', 'OT (Yes/No)', 'No Pay (Yes/No)', 'Batch Applicable (Yes/No)', 'Remarks'
];

export const downloadComponentTemplate = () => {
  const wb = XLSX.utils.book_new();
  const sampleData = [{
    'Component Code *': 'FA001',
    'Component Name *': 'Basic Salary',
    'Description': 'Standard basic salary',
    'Salary Slip Description': 'Basic',
    'Earning Category (Fixed/Variable/None)': 'Fixed',
    'Component Type (Allowance/Deduction/Contribution) *': 'Allowance',
    'Account Number': '1234567890',
    'Calculation Method (Fixed/Daily-Based) *': 'Fixed',
    'Default Amount': 50000,
    'Default Percentage': '',
    'Taxable (Yes/No)': 'Yes',
    'EPF (Yes/No)': 'Yes',
    'ETF (Yes/No)': 'Yes',
    'OT (Yes/No)': 'Yes',
    'No Pay (Yes/No)': 'No',
    'Batch Applicable (Yes/No)': 'No',
    'Remarks': 'Added via bulk import',
  }];

  const ws = XLSX.utils.json_to_sheet(sampleData, { header: COLUMNS });
  // Make columns wide enough to read
  ws['!cols'] = COLUMNS.map(() => ({ wch: 25 }));
  
  XLSX.utils.book_append_sheet(wb, ws, 'Components');
  XLSX.writeFile(wb, 'Payroll_Components_Import_Template.xlsx');
};

const parseBool = (val) => {
  const s = String(val ?? '').trim().toLowerCase();
  return s === 'yes' || s === 'y' || s === 'true' || s === '1';
};

export const readComponentWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) return [];

  const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '' });
  
  return rawRows.map((row, idx) => ({
    _row: idx + 2,
    code: String(row['Component Code *'] ?? '').trim(),
    name: String(row['Component Name *'] ?? '').trim(),
    description: String(row['Description'] ?? '').trim(),
    salarySlipDescription: String(row['Salary Slip Description'] ?? '').trim(),
    earningCategory: String(row['Earning Category (Fixed/Variable/None)'] ?? '').trim(),
    type: String(row['Component Type (Allowance/Deduction/Contribution) *'] ?? '').trim(),
    accountNo: String(row['Account Number'] ?? '').replace(/\D/g, ''), // Strip non-numeric
    calculationMethod: String(row['Calculation Method (Fixed/Daily-Based) *'] ?? '').trim(),
    defaultAmount: String(row['Default Amount'] ?? '').trim(),
    defaultPercentage: String(row['Default Percentage'] ?? '').trim(),
    taxable: parseBool(row['Taxable (Yes/No)']),
    isEpf: parseBool(row['EPF (Yes/No)']),
    isEtf: parseBool(row['ETF (Yes/No)']),
    isOT: parseBool(row['OT (Yes/No)']),
    isNoPay: parseBool(row['No Pay (Yes/No)']),
    isBatch: parseBool(row['Batch Applicable (Yes/No)']),
    remarks: String(row['Remarks'] ?? '').trim(),
  }));
};

export const validateComponentRow = (row, existingComponents, duplicateInFileSet) => {
  const errors = [];
  const lowerCode = row.code.toLowerCase();

  // Code Val
  if (!row.code) errors.push('Component Code is required');
  else if (duplicateInFileSet.has(lowerCode)) errors.push(`Duplicate Code in Excel`);
  else if (existingComponents.some(c => String(c.code).toLowerCase() === lowerCode)) errors.push(`Code already exists in system`);

  if (!row.name) errors.push('Component Name is required');

  // Type Map
  let mappedType = 'E';
  const t = row.type.toLowerCase();
  if (t.includes('deduct')) mappedType = 'D';
  else if (t.includes('contribut')) mappedType = 'C';
  else if (!t.includes('allowance')) errors.push('Invalid Component Type');

  // Category Map
  let mappedCat = '';
  const c = row.earningCategory.toLowerCase();
  if (c.includes('fixed') || c === 'f') mappedCat = 'F';
  else if (c.includes('variable') || c === 'v') mappedCat = 'V';

  // Calc Method Map
  let mappedCalc = 'F';
  const calc = row.calculationMethod.toLowerCase();
  if (calc.includes('daily') || calc.includes('percent')) mappedCalc = 'P';
  else if (!calc.includes('fixed')) errors.push('Invalid Calculation Method');

  // Amounts
  let amt = 0;
  if (mappedCalc === 'F' && row.defaultAmount) {
    amt = Number(row.defaultAmount.replace(/,/g, ''));
    if (Number.isNaN(amt)) errors.push('Default Amount is invalid');
    else if (amt < 0) errors.push('Default Amount cannot be negative');
  }

  let pct = 0;
  if (mappedCalc === 'P' && row.defaultPercentage) {
    pct = Number(row.defaultPercentage);
    if (Number.isNaN(pct) || pct <= 0 || pct > 100) errors.push('Default Percentage must be between 0.01 and 100');
  }

  return {
    values: {
      ...row,
      type: mappedType,
      earningCategory: mappedCat,
      calculationMethod: mappedCalc,
      defaultAmount: amt,
      defaultPercentage: pct,
      paymentType: "M" // Default mapping 
    },
    errors,
  };
};